import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useProjectMemberRepository } from '../../../../services/adaptors/projectMemberRepositoryAdaptor'
import { useSprintRepository } from '../../../../services/adaptors/sprintRepositoryAdaptor'
import { useTeamRepository } from '../../../../services/adaptors/teamRepositoryAdaptor'
import { WbsItemFormModel } from '../model'
import { ReferencedEntity } from '../../../../domain/value-object/ReferencedEntity'

export type WbsItemPropertySearch = {
  searchTeam: (searchText: string) => Promise<ReferencedEntity[]>
  searchMember: (searchText: string) => Promise<ReferencedEntity[]>
  searchSprint: (searchText: string) => Promise<ReferencedEntity[]>
}
export const useWbsItemPropertySearch = (
  projectUuid: string,
  formModel: WbsItemFormModel
): WbsItemPropertySearch => {
  const teamsCache = useRef<ReferencedEntity[] | undefined>(undefined)
  const { fetchListAsReferencedEntity: fetchTeams } = useTeamRepository()
  const searchTeam = useCallback(
    async (searchText: string) => {
      let candidates = teamsCache.current
      if (!candidates) {
        candidates = await fetchTeams(projectUuid)
        teamsCache.current = candidates
      }

      if (searchText) {
        return candidates.filter(sprint => sprint.name.includes(searchText))
      }
      return candidates
    },
    [fetchTeams, projectUuid]
  )

  const { searchAsReferencedEntity: searchProjectMembers } =
    useProjectMemberRepository()
  const searchMember = useCallback(
    async (searchText: string) => {
      return searchProjectMembers(projectUuid, searchText)
    },
    [projectUuid, searchProjectMembers]
  )

  // Use ref of team to prevent re-render.
  const currentTeam = useRef<ReferencedEntity | undefined>(undefined)
  useEffect(() => {
    currentTeam.current = formModel.team
  }, [formModel.team])

  const sprintsCache = useRef<{ [teamUuid: string]: ReferencedEntity[] }>({})
  const { fetchActiveAsReferencedEntity: fetchSprints } = useSprintRepository()
  const searchSprint = useCallback(
    async (searchText: string) => {
      if (!currentTeam.current) return []

      const teamUuid = currentTeam.current.uuid
      let candidates = sprintsCache.current[teamUuid]
      if (!candidates) {
        candidates = await fetchSprints(teamUuid)
        sprintsCache.current[teamUuid] = candidates
      }
      if (searchText) {
        return candidates.filter(sprint => sprint.name.includes(searchText))
      }
      return candidates
    },
    [fetchSprints]
  )

  const functions = useMemo(
    () => ({
      searchTeam,
      searchMember,
      searchSprint,
    }),
    [searchMember, searchSprint, searchTeam]
  )
  return functions
}
